import { unLinkWorkOrderParts } from "@/pages/work-orders/repositorie/WorkOrdersRepositorie";
import { useWorkOrderModalsContext } from "@/shared/work-orders-modals/context/work-order-modals.context";
import type { GridApi } from "ag-grid-community";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

export const useWorkOrderPartsActionsAndButtons = ({
	gridRef,
	searchWorkOrderParts,
	setRemovePartError,
	WorkOrderId,
}: {
	WorkOrderId: number | null;
	newMode?: boolean;
	gridRef: GridApi | null;
	searchWorkOrderParts: Function;
	setRemovePartError: (err: string) => void | null;
	setListMessage: (err: string) => void | null;
}) => {
	const {
		state: { rowSelectedInPart, workOrderPartsList },
		actions: {
			setFirstTime,
			setRowSelectedInPart,
			setPartSearch,
			setDataPartSelected,
			setWorkOrderPartsList,
			setListPartsSelected,
		},
	} = useWorkOrderModalsContext();

	const [searchParams] = useSearchParams();
	const [refreshTrigger, setRefreshTrigger] = useState(1);
	const isMountRef = useRef(true);

	// BUTTONS STATES
	const [addButton, setAddButton] = useState<boolean>(true);
	const [deleteButton, setDeleteButton] = useState<boolean>(false);
	const [saveButton, setSaveButton] = useState<boolean>(false);
	const [discardButton, setDiscardButton] = useState<boolean>(false);
	const [deleteOpen, setDeleteOpen] = useState(false);

	const refresh = useCallback(() => {
		if (!isMountRef.current) {
			const updatedList = workOrderPartsList.filter(
				(item: any) => item.id !== -1,
			);

			setWorkOrderPartsList(updatedList);
			setFirstTime(true);
			setSaveButton(false);
			setDiscardButton(false);
			setAddButton(true);
			setDeleteButton(false);
			setDeleteOpen(false);
			setPartSearch("");
			setDataPartSelected(undefined);
			searchWorkOrderParts();
		}
	}, [searchParams, WorkOrderId]);

	const cancelAddPart = useCallback(() => {
		const updatedList = workOrderPartsList.filter(
			(item: any) => item.id !== -1,
		);

		setWorkOrderPartsList(updatedList);
		setDataPartSelected(null);
		setAddButton(true);
		setSaveButton(false);
		setDiscardButton(false);
		setDeleteButton(false);
		setPartSearch("");
	}, [
		workOrderPartsList,
		setWorkOrderPartsList,
		setDataPartSelected,
		setAddButton,
		setSaveButton,
		setDiscardButton,
		setDeleteButton,
		setPartSearch,
	]);

	const addRow = useCallback(() => {
		setAddButton(false);
		setSaveButton(true);
		setDiscardButton(true);
		setDeleteButton(false);
		const newData = { id: -1, WorkOrderId: WorkOrderId };
		const currentData = workOrderPartsList ? workOrderPartsList : [];
		const data = [newData, ...currentData];
		setWorkOrderPartsList(data);
	}, [workOrderPartsList, WorkOrderId]);

	const removeParts = useCallback(
		async (partsToRemove?: any[]) => {
			let formattedParts: { PartName: string; IsAssetResource: boolean }[] = [];

			if (partsToRemove && partsToRemove.length > 0) {
				formattedParts = partsToRemove.map((part: any) => ({
					PartName: part.data ? part.data.PartName : part.PartName,
					IsAssetResource: part.data
						? part.data.IsAssetResource
						: part.IsAssetResource,
				}));
			} else if (rowSelectedInPart) {
				formattedParts = [
					{
						PartName: rowSelectedInPart.PartName,
						IsAssetResource: rowSelectedInPart.IsAssetResource,
					},
				];
			}

			if (formattedParts.length > 0) {
				const res = await unLinkWorkOrderParts(
					WorkOrderId as number,
					formattedParts,
				);
				if (res.ok) {
					refresh();
				} else {
					setRemovePartError(res.message);
				}
			}

			setRowSelectedInPart(null);
			setDeleteOpen(false);
			setAddButton(true);
			setSaveButton(false);
			setDiscardButton(false);
			setDeleteButton(false);
			setPartSearch("");
			setListPartsSelected([]);

			if (gridRef) {
				gridRef.deselectAll();
			}
		},
		[
			WorkOrderId,
			unLinkWorkOrderParts,
			refresh,
			setRemovePartError,
			rowSelectedInPart,
			setRowSelectedInPart,
			setDeleteOpen,
			setAddButton,
			setSaveButton,
			setDiscardButton,
			setDeleteButton,
			setPartSearch,
			setListPartsSelected,
			gridRef,
		],
	);

	useEffect(() => {
		refresh();
	}, [refreshTrigger]);

	useEffect(() => {
		isMountRef.current = false;
	}, []);

	return {
		refresh,
		cancelAddPart,
		removeParts,
		addRow,
		addButton,
		deleteButton,
		saveButton,
		discardButton,
		deleteOpen,
		setSaveButton,
		setDiscardButton,
		setAddButton,
		setDeleteButton,
		setDeleteOpen,
	};
};
